import { motion } from 'framer-motion';
import Lottie from 'lottie-react';
import colorfulAnimation from '../../assets/animations/dashboard-loading.json';
import grayAnimation from '../../assets/animations/dashboard-loading-gray.json';
import styles from '@/styles/features/auth/auth-pages.module.css';

/**
 * Unified Mira Loader — Lottie animations everywhere.
 *
 * Props:
 *   message    — text shown below animation
 *   loaderText — legacy alias for message
 *   subtitle   — secondary line below message (FullScreen only)
 *   FullScreen — true (default) → full-page overlay; false → inline
 *   variant    — 'colorful' | 'gray' | 'button'
 *               default: 'colorful' when FullScreen, 'gray' when inline
 */
const Loader = ({ loaderText, FullScreen = true, variant, message, subtitle }) => {
  const resolvedVariant = variant ?? (FullScreen ? 'colorful' : 'gray');
  const text = message ?? loaderText ?? '';
  const animationData = resolvedVariant === 'gray' ? grayAnimation : colorfulAnimation;

  // ── Tiny inline button spinner ─────────────────────────────────────────────
  if (resolvedVariant === 'button') {
    return (
      <span className="inline-flex items-center justify-center gap-1" role="status" aria-live="polite">
        <span className="inline-block w-6 h-6">
          <Lottie animationData={grayAnimation} loop className={styles.fullSizeLottie} />
        </span>
        {text ? <span className="text-sm font-medium">{text}</span> : null}
      </span>
    );
  }

  // ── Full-screen overlay ────────────────────────────────────────────────────
  if (FullScreen) {
    return (
      <div className="min-h-screen w-full flex flex-col items-center justify-center bg-white">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.25 }}
          className="flex flex-col items-center"
        >
          {/* The dark wordmark, not the white one: this overlay is on white.
              Same artwork, the variant Figma draws for a light surface. */}
          <img
            src="/opus-wordmark-dark.png"
            alt="Opus Capital Markets Consultants"
            className="h-10 w-auto object-contain"
          />
          <div className="w-56 h-56 -mt-14 -mb-14">
            <Lottie animationData={animationData} loop className={styles.fullSizeLottie} />
          </div>
          {text && <p className={`mt-0 font-medium ${styles.brandText}`}>{text}</p>}
          {subtitle && <p className="text-sm text-gray-400">{subtitle}</p>}
        </motion.div>
      </div>
    );
  }

  // ── Inline (FullScreen=false) ──────────────────────────────────────────────
  return (
    <div className="inline-flex items-center gap-2" role="status" aria-live="polite">
      <div className={text ? 'w-10 h-10' : 'w-12 h-12'}>
        <Lottie animationData={animationData} loop className={styles.fullSizeLottie} />
      </div>
      {text && <span className={`text-sm font-medium ${styles.brandText}`}>{text}</span>}
      {subtitle && <span className="text-xs text-gray-400">{subtitle}</span>}
    </div>
  );
};

export default Loader;
